import { Avatar } from '@understory-io/leaf/avatar';
import { Badge } from '@understory-io/leaf/badge';
import { css } from '@understory-io/leaf/css';
import { type IntlShape, useIntl } from 'react-intl';
import { cx } from '~/styled-system/css';
import { normaliseSpokenLocale } from '~/lib/localization/locale';

/**
 * The locale to hand `<Avatar flagLocale>`. Leaf picks the flag from the
 * region, and the event service sends bare codes (`da`, `en`), which would
 * leave the avatar on its initials fallback. Unknown codes pass through.
 */
export function toFlagLocale(locale: string): string {
  return normaliseSpokenLocale(locale) ?? locale;
}

export function StackedFlagAvatars({ locales }: { locales: string[] }) {
  return (
    <div
      className={css({
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'end',
        gap: 'micro',
        py: 'tiny',
      })}
    >
      {locales.map((x) => (
        <Avatar key={x} size="tiny" flagLocale={toFlagLocale(x)} />
      ))}
    </div>
  );
}

/**
 * The languages an event is held in, one badge per language with its flag
 * and its name in the visitor's language. A flag alone is not enough: guests
 * have booked a tour in the wrong language without noticing (US-13394), and
 * the region a flag stands for is not the language anyway.
 */
export function EventLanguages({
  locales,
  layout = 'inline',
  muted = false,
  className,
}: {
  locales: string[];
  /** `inline` sits beside the date and refuses to shrink; `row` is for a
   *  line of its own, where several badges may wrap. */
  layout?: 'inline' | 'row';
  /** Dim the badges along with the rest of a sold-out card. */
  muted?: boolean;
  className?: string;
}) {
  const intl = useIntl();
  return (
    <span
      aria-hidden="true"
      className={cx(
        css({
          display: 'inline-flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 'xsmall',
          flexShrink: 0,
        }),
        layout === 'row' && css({ flexWrap: 'wrap' }),
        className
      )}
    >
      {locales.map((locale) => (
        <Badge
          key={locale}
          appearance={muted ? 'disabled' : 'neutral'}
          size="small"
          icon={<Avatar size="tiny" flagLocale={toFlagLocale(locale)} />}
        >
          {languageName(intl, locale)}
        </Badge>
      ))}
    </span>
  );
}

/**
 * "English" for `en-GB`: named by the language subtag only, so it does not
 * read "British English" — the region is a flag-picking detail, not something
 * the guest chose.
 */
export function languageName(intl: IntlShape, locale: string): string {
  const language = locale.split('-')[0]!;
  const name = intl.formatDisplayName(language, { type: 'language' });
  return capitalizeFirst(name ?? language, intl.locale);
}

/** "English, German" for `['en-GB', 'de-DE']`. */
export function formatLanguageNames(
  intl: IntlShape,
  locales: readonly string[]
): string {
  return locales.map((locale) => languageName(intl, locale)).join(', ');
}

// `Intl.DisplayNames` returns lowercase names in some locales (Danish
// "engelsk"); capitalise with the UI locale's letter rules.
function capitalizeFirst(label: string, locale: string): string {
  if (!label) return label;
  return label.charAt(0).toLocaleUpperCase(locale) + label.slice(1);
}
